import { motion } from "framer-motion";
import { Shield, Users, MapPin, Award, Heart, Target, CheckCircle2 } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import SectionHeading from "@/components/SectionHeading";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const stats = [
  { value: "10,000+", label: "Happy Customers in Pondicherry" },
  { value: "50+", label: "Cabs & SUVs in Fleet" },
  { value: "100+", label: "Background-Verified Drivers" },
  { value: "4.9★", label: "Google Rating — Pondicherry" },
];

const About = () => (
  <Layout>
    <SEOHead
      title="About Pondicherry Taxi | Best Cab Service in Puducherry — Airport, Outstation & City Tours"
      description="Pondicherry Taxi — Puducherry's most trusted cab service. 24/7 taxi for airport pickup, Pondicherry to Chennai cab, outstation trips, local sightseeing and corporate travel. Affordable fixed fares, verified drivers."
      keywords="about Pondicherry Taxi, taxi company Puducherry, cab service Pondicherry, best taxi services in pondicherry, taxi services in pondicherry, 24/7 taxi services, Affordable Taxi Services in Pondicherry, Affordable Taxi Fares in Pondicherry, Pondicherry local sightseeing taxi, Pondicherry city tour packages, Pondicherry sightseeing cab service, Pondicherry airport taxi service, Airport pickup and drop Pondicherry, Chennai to Pondicherry airport taxi, Pondicherry to Chennai cab, Daily pickup drop service, Office pickup drop taxi, Chennai Airport drop taxi service, Pondicherry Airport drop Taxi service, Tours and travels in pondicherry, One-way drop taxi service in pondicherry, Local Pondicherry sightseeing(City Tour), Airport transfer (Hotels)pondicherry, Outstation trips pondicherry, corporate cab service in pondicherry,taxi in Pondicherry, cab booking Pondicherry, best taxi service Pondicherry, Pondicherry airport taxi, Pondicherry to Chennai cab, Chennai to Pondicherry taxi, outstation taxi Pondicherry, 24/7 taxi Pondicherry, affordable taxi Pondicherry, local sightseeing taxi Pondicherry, one way drop taxi Pondicherry, corporate cab service Pondicherry, Puducherry cab service near me,Best Taxi Service in Pondicherry, Trusted Taxi Service in Pondicherry, Pondicherry Taxi Company, Reliable Cab Service in Pondicherry, Pondicherry Tours and Travels"
      canonical="https://pondicherry.taxi/about"
    />

    <section className="pt-28 pb-16 section-padding bg-gradient-to-b from-primary/5 to-transparent">
      <div className="container-custom text-center">
        <motion.h1 {...fadeUp} className="text-4xl sm:text-5xl font-bold">
          About <span className="text-gradient">Pondicherry Taxi</span> — Puducherry's Most Trusted Cab Service
        </motion.h1>
        <motion.p {...fadeUp} className="mt-4 text-muted-foreground max-w-2xl mx-auto">
          Puducherry's most trusted taxi service — 24/7 cab booking for airport pickup, Pondicherry to Chennai, outstation trips, local sightseeing and corporate travel at affordable fixed fares.
        </motion.p>
      </div>
    </section>

    {/* Stats */}
    <section className="section-padding">
      <div className="container-custom">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {stats.map((s, i) => (
            <motion.div key={s.label} {...fadeUp} transition={{ delay: i * 0.1 }} className="glass-card rounded-2xl p-6 text-center">
              <p className="text-3xl font-bold text-gradient">{s.value}</p>
              <p className="text-sm text-muted-foreground mt-1">{s.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Story */}
        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          <motion.div {...fadeUp}>
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20 mb-4">Pondicherry Taxi — Our Story</span>
            <h2 className="text-3xl font-bold text-foreground mb-4">Built on Trust — Pondicherry's Most Reliable Cab Service Since Day One</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Pondicherry Taxi was founded with a simple mission — to give every resident and visitor in Puducherry a cab service they can trust completely. Starting with just 5 cars, we have grown into Pondicherry's most recommended taxi service, covering airport pickups, city tours, outstation trips and 24/7 cab bookings across Puducherry.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Our deep local expertise sets us apart. Our drivers know every street, landmark and hidden gem in Pondicherry — from the cobblestoned lanes of White Town to the spiritual serenity of Auroville. Whether you need a local sightseeing taxi, Pondicherry airport transfer or a one-way drop cab to Chennai, our team makes every ride an experience.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Today we serve 10,000+ happy customers, operate 50+ well-maintained cabs and employ 100+ verified drivers. Whether it's a quick Promenade Beach ride, a Pondicherry to Chennai cab, an outstation trip to Bangalore or Tirupati, or a corporate pickup — Pondicherry Taxi is the name locals and tourists trust for 24/7 reliable service.
            </p>
          </motion.div>
          <motion.div {...fadeUp} transition={{ delay: 0.2 }} className="glass-card rounded-2xl p-10 flex items-center justify-center min-h-[350px]">
            <MapPin className="w-32 h-32 text-primary/20" />
          </motion.div>
        </div>

        {/* Mission & Vision */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          <motion.div {...fadeUp} className="glass-card-hover rounded-2xl p-8">
            <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
              <Target className="w-7 h-7 text-primary" />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-3">  Our Mission — Affordable Taxi Service in Pondicherry for Everyone</h3>
            <p className="text-muted-foreground leading-relaxed">
              To make safe, affordable and reliable cab booking accessible to every traveler in Pondicherry — for airport transfers, one-way drop taxis, outstation trips and daily office pickup. We set the gold standard for taxi services in Puducherry with transparent fixed fares and zero hidden charges.
            </p>
          </motion.div>
          <motion.div {...fadeUp} transition={{ delay: 0.1 }} className="glass-card-hover rounded-2xl p-8">
            <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
              <Heart className="w-7 h-7 text-primary" />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-3"> Our Vision — First Choice Cab Service in Puducherry</h3>
            <p className="text-muted-foreground leading-relaxed">
              To be the first name that comes to mind for taxi service in Pondicherry — whether for a local city tour, Chennai to Pondicherry airport taxi, or 24/7 outstation cab. We envision every journey in Puducherry to be comfortable, safe and memorable.
            </p>
          </motion.div>
        </div>

        {/* Why Trust Us */}
        <SectionHeading badge="Trust" title="Why Customers" highlight="Trust Us" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: Shield, title: "Verified Drivers", desc: "Every Pondicherry taxi driver undergoes thorough background checks and professional training before joining our fleet." },
            { icon: Award, title: "Top-Rated Service", desc: "4.9-star Google rating from thousands of satisfied customers across Pondicherry and Puducherry." },
            { icon: Users, title: "Local Expertise", desc: "Our team knows every road, attraction and shortcut in Pondicherry — from Auroville to Promenade Beach to the airport." },
            { icon: MapPin, title: "Wide Coverage", desc: "Local Pondicherry sightseeing, airport transfers, Pondicherry to Chennai cab and outstation trips across South India." },
            { icon: CheckCircle2, title: "Transparent Pricing", desc: "Affordable fixed taxi fares in Pondicherry — no hidden charges, no surge pricing for airport, local or outstation rides." },
            { icon: Heart, title: "Customer First", desc: "Your comfort and safety are our top priority — on every local, airport and outstation cab ride in Pondicherry." },
          ].map((item, i) => (
            <motion.div key={item.title} {...fadeUp} transition={{ delay: i * 0.1 }} className="glass-card-hover rounded-2xl p-6">
              <item.icon className="w-8 h-8 text-primary mb-4" />
              <h3 className="text-foreground font-semibold mb-2">{item.title}</h3>
              <p className="text-sm text-muted-foreground">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  </Layout>
);

export default About;
